import type { AdminResourceItemDef } from "./resource-catalog.types";

export const ADMIN_RESOURCE_CATALOG_OVERVIEW: readonly AdminResourceItemDef[] = [
  // ── Visibility ──────────────────────────────────────────────────────────────
  {
    id: "dashboard",
    name: "Dashboard",
    description: "Admin panel home dashboard",
    category: "Visibility"
  },
  {
    id: "health",
    name: "Health",
    description: "System health monitoring",
    category: "Visibility",
    blocks: [
      { id: "internal", name: "Internal Services", description: "Database, Redis, BullMQ health" },
      {
        id: "external",
        name: "External APIs",
        description: "OpenAI, Anthropic, Stripe connectivity"
      },
      {
        id: "oauth",
        name: "OAuth Connections",
        description: "Token health and refresh status",
        actions: [
          {
            id: "view",
            name: "View OAuth Health",
            description: "View connection status",
            unserved:
              "Nothing reads this path. Viewing is gated one level up, by " +
              "`<AdminGate resource='admin:health:oauth' action='read'>` in OAuthHealthSection; " +
              "only `refresh` below is addressed at its own path."
          },
          { id: "refresh", name: "Refresh Tokens", description: "Trigger token refresh" }
        ]
      }
    ]
  },
  {
    id: "observability",
    name: "Observability",
    description: "Metrics, monitoring, and system insights",
    category: "Visibility",
    actions: [
      { id: "view", name: "View Metrics", description: "View system metrics and dashboards" },
      { id: "export", name: "Export Data", description: "Export metrics data" }
    ],
    blocks: [
      { id: "http", name: "HTTP Metrics", description: "Request rates and latency" },
      { id: "ai", name: "AI Usage", description: "Token usage by provider" },
      { id: "workflows", name: "Workflows", description: "Workflow execution stats" },
      { id: "queues", name: "Queues", description: "BullMQ queue depths" }
    ]
  },
  {
    id: "echo",
    name: "Echo",
    description: "Cross-org audit trail viewer and global configuration for the Echo subsystem",
    category: "Visibility",
    // Deliberately gated by `admin:organizations`, not by an `admin:echo` of its own:
    // any admin who can read an org's details can read its audit trail, and adding a
    // dedicated resource would make every existing policy opt in to keep working
    // (see the permission map in admin-navigation.ts, and the guard on
    // admin-echo.controller.ts). It keeps its sidebar entry; it is not grantable.
    navOnly: true
  },
  {
    id: "issues",
    name: "Issues",
    description: "Operational issues auto-detected across all organizations",
    category: "Visibility",
    actions: [
      { id: "list", name: "View Issues", description: "View all operational issues" },
      { id: "resolve", name: "Resolve Issue", description: "Mark issue as resolved" },
      { id: "ignore", name: "Ignore Issue", description: "Mark issue as ignored" }
    ]
  },
  {
    id: "usage",
    name: "Usage Analytics",
    description: "Platform-wide token usage, cost leaderboard, channel adoption",
    category: "Visibility",
    actions: [{ id: "view", name: "View Usage", description: "View platform usage analytics" }],
    blocks: [
      { id: "leaderboard", name: "Org Leaderboard", description: "Top organizations by spend" },
      { id: "channels", name: "Channel Adoption", description: "Usage per deployment channel" },
      { id: "sources", name: "Usage Sources", description: "Deployment vs Playground vs Workflow" },
      { id: "models", name: "Model Usage", description: "Cost per AI model" }
    ]
  },
  {
    id: "code-interpreter-packages",
    name: "Package Usage",
    description: "Cross-org runtime package install tracking for snapshot optimization",
    category: "Visibility"
  },
  {
    id: "cue",
    name: "Cue Snapshot",
    description: "Rebuild and monitor the Cue Daytona snapshot",
    category: "Visibility"
  },

  // ── Vibe ────────────────────────────────────────────────────────────────────
  {
    id: "vibe-tenant-cluster",
    name: "Vibe",
    description:
      "Operator console for the Vibe platform — provision per-org clusters and read fleet expense",
    category: "Vibe",
    actions: [
      {
        id: "provision",
        name: "Provision cluster",
        description: "Onboard an org into its own dedicated Vibe cluster"
      },
      {
        id: "disable",
        name: "Disable cluster",
        description: "Opt an org's cluster back out (retained through the grace window)"
      }
    ]
  },
  // Vibe operator sub-surfaces. These guard individual automation/billing endpoints
  // and tabs of the Vibe console; they are grantable (no godmode-only skeletons) but
  // have no dedicated sidebar entry of their own.
  {
    // Deliberately NOT folded into `vibe-tenant-cluster`. That resource's write
    // permission is held by anyone who provisions or repairs a SINGLE tenant;
    // this one flips a setting on the Cloudflare zone every tenant shares, where
    // the blast radius of a wrong press is the whole fleet's public HTTPS. Same
    // console, same operator most days — but granting the per-tenant lever must
    // not silently grant the fleet-wide one.
    id: "vibe-zone",
    name: "Vibe Shared Zone",
    description:
      "Fleet-wide edge settings on the Cloudflare zone every Vibe tenant's hostnames hang off",
    category: "Vibe"
  },
  {
    id: "vibe-margin-recon",
    name: "Vibe Expense & Margin",
    description: "Fleet expense reconciliation and margin reporting (the Vibe Expense tab)",
    category: "Vibe"
  },
  {
    id: "vibe-invoicing",
    name: "Vibe Invoicing",
    description: "Generate and manage per-org Vibe invoices",
    category: "Vibe"
  },
  {
    id: "vibe-consumption-cap",
    name: "Vibe Consumption Caps",
    description: "Per-org consumption caps for the Vibe platform",
    category: "Vibe"
  },
  {
    id: "vibe-cost-safety",
    name: "Vibe Cost Safety",
    description: "Cost-safety thresholds and kill-switches for Vibe spend",
    category: "Vibe"
  },
  {
    id: "vibe-deployment",
    name: "Vibe Deployments",
    description: "Operator control over per-tenant Vibe deployments",
    category: "Vibe"
  },
  {
    id: "vibe-deployment-runner",
    name: "Vibe Deployment Runner",
    description: "Trigger and manage Vibe deployment runner jobs",
    category: "Vibe"
  },
  {
    id: "vibe-build-job",
    name: "Vibe Build Jobs",
    description: "Operator control over Vibe build jobs",
    category: "Vibe"
  },
  {
    id: "vibe-build-runner",
    name: "Vibe Build Runner",
    description: "Trigger and manage Vibe build runner jobs",
    category: "Vibe"
  },
  {
    id: "vibe-rating-preview",
    name: "Vibe Rating Preview",
    description: "Preview Vibe usage ratings before invoicing",
    category: "Vibe"
  },
  {
    id: "vibe-app",
    name: "Vibe Apps",
    description:
      "Fleet-wide view of Vibe apps that cannot deploy — apps with no git project attached",
    category: "Vibe",
    actions: [
      {
        id: "attach-source",
        name: "Attach source",
        description: "Point an app with no source at an existing git project in its own org"
      }
    ]
  },
  {
    // Deliberately NOT folded into `vibe-app`. That resource governs the join
    // between an app and a source it already has; this one re-materializes the
    // SOURCE ITSELF, which for a READY project means replacing a repository
    // that claims to be serving with a fresh empty one under the same name.
    // Granting an operator the ability to repair a dangling app must not
    // silently grant them the ability to blank a project's remote.
    id: "vibe-git-project",
    name: "Vibe Git Projects",
    description:
      "Operator repair for Vibe git projects — force a FAILED or READY project back into the tenant agent's reconcile loop, and list the projects builds cannot clone yet (no read-only credential)",
    category: "Vibe"
  },

  // ── Customers ───────────────────────────────────────────────────────────────
  {
    id: "organizations",
    name: "Organizations",
    description: "Organization and user management",
    category: "Customers",
    actions: [
      {
        id: "list",
        name: "View Organizations",
        description: "View all organizations",
        unserved:
          "Nothing reads this path. The organization routes guard the item root " +
          "`admin:organizations` and express list-versus-write through the RWX bitmask; " +
          "`rate-limits` below is the only subtree here addressed at its own paths."
      },
      {
        id: "sync",
        name: "Sync Organizations",
        description: "Sync with Clerk",
        unserved: "Nothing reads this path. The sync route guards `admin:organizations` with write."
      }
    ],
    blocks: [
      {
        id: "clerk-sync",
        name: "Clerk Sync",
        description: "Sync organizations and users from Clerk",
        unserved:
          "Nothing reads this block or either action below. The Clerk sync routes guard " +
          "`admin:organizations`, so granting anything under here reaches no endpoint.",
        actions: [
          { id: "sync-orgs", name: "Sync Organizations", description: "Sync orgs from Clerk" },
          { id: "sync-users", name: "Sync Users", description: "Sync users from Clerk" }
        ]
      },
      {
        id: "billing",
        name: "Billing",
        description: "Organization billing management",
        unserved:
          "Nothing reads this block or either action below. Billing is served under " +
          "`admin:organizations` (admin-navigation.ts maps /admin/billing to it), so a grant " +
          "here reaches no endpoint and hides no control.",
        actions: [
          { id: "view", name: "View Billing", description: "View billing info" },
          { id: "manage", name: "Manage Billing", description: "Modify plans" }
        ]
      },
      {
        id: "rate-limits",
        name: "Rate Limits",
        description: "Per-organization custom rate limits",
        actions: [
          { id: "list", name: "View Rate Limits", description: "View organization rate limits" },
          {
            id: "view",
            name: "View Details",
            description: "View rate limit details for an organization"
          },
          {
            id: "edit",
            name: "Set Custom Limit",
            description: "Set custom rate limit for an organization"
          },
          {
            id: "reset",
            name: "Reset to Default",
            description: "Reset organization to tier default"
          }
        ]
      }
    ]
  },
  {
    id: "teams",
    name: "Teams",
    description: "Cross-org team administration",
    category: "Customers"
  }
] as const;
